export default class CartIncentives extends HTMLElement {
  constructor() {
    super();
    this.cartSubtotal = null;
    this.nextThreshold = null;

    const dataEl = this.querySelector('[data-incentives]');
    this.thresholdData = dataEl ? JSON.parse(dataEl.textContent) : {};
    this.thresholdData.sort((a, b) => a.threshold - b.threshold);

    this.progressThreshold = this.querySelector('.progress-threshold');
    this.init();
  }

  setupIcons() {
    // reorder icons on container based on their threshold

    const icons = this.querySelectorAll('[data-icon]');
    const iconContainer = this.querySelector('.icons');

    if (icons.length === 0) return;

    const iconArray = Array.from(icons);
    iconArray.sort((a, b) => {
      const aThreshold = parseInt(a.dataset.threshold, 10);
      const bThreshold = parseInt(b.dataset.threshold, 10);
      return aThreshold - bThreshold;
    });

    iconArray.forEach((icon) => {
      iconContainer.appendChild(icon);
    });
  }

  renderProgress(cartData) {
    const progressText = this.querySelector('[data-progress-text]');

    this.cartSubtotal = this.calculateCartTotalWithGWP(
      cartData ?? document.querySelector('[data-cart-data]'),
    );
    let currentProgress;
    let progressWidth = '0%';

    if (this.thresholdData.length) {
      currentProgress = this.calculateProgress(
        this.cartSubtotal,
        this.thresholdData,
      );
      progressWidth =
        this.thresholdData[0].threshold > this.cartSubtotal
          ? `${currentProgress}%`
          : `calc(${currentProgress}%)`;

      if (currentProgress === 0) {
        progressText.textContent = this.thresholdData[0].negative_text.replace(
          '{amount}',
          // eslint-disable-next-line max-len
          `$${(this.thresholdData[0].threshold - this.cartSubtotal).toFixed(2)}`,
        );
      } else if (currentProgress === 100) {
        const maxThreshold = this.thresholdData.reduce(
          (a, b) => b.threshold > a.threshold ? b : a
        );
        progressText.textContent = maxThreshold.positive_text;
      } else if (this.nextThreshold) {
        if (this.cartSubtotal < this.nextThreshold.threshold) {
          progressText.textContent = this.nextThreshold.negative_text.replace(
            '{amount}',
            `$${(this.nextThreshold.threshold - this.cartSubtotal).toFixed(2)}`,
          );
        } else {
          progressText.textContent = this.nextThreshold.positive_text;
        }
      }
    }

    this.querySelector('[data-progress-bar]').style.width = progressWidth;

    const icons = this.querySelectorAll('[data-icon]');
    icons.forEach((icon, index) => {
      const threshold = parseInt(icon.dataset.threshold, 10);
      const position =
        ((threshold - this.thresholdData[0].threshold) /
          (this.thresholdData[this.thresholdData.length - 1].threshold -
            this.thresholdData[0].threshold)) *
        100;

      const isLastItem = index === this.thresholdData.length - 1;
      const leftStyle = isLastItem
        ? `calc(${position}% - 25px)`
        : `${position}%`; // Adjusted the position based on whether it's the last item

      let finalPosition;
      if (this.thresholdData.length === 1) {
        finalPosition = `calc(100% - 25px)`;
      } else {
        finalPosition = leftStyle;
      }

      if (threshold <= this.cartSubtotal) {
        icon.classList.add('active');
      } else {
        icon.classList.remove('active');
      }

      // eslint-disable-next-line no-param-reassign
      icon.style.left = finalPosition;
    });

    const cartDataElement = cartData ?? document.querySelector('[data-cart-data]');
    const parsedCartData = cartDataElement ?
      JSON.parse(cartDataElement.textContent) : null;

    if (this.thresholdData.length === 0 || !parsedCartData?.items?.length) {
      this.progressThreshold.classList.add('hidden');
      this.progressThreshold.classList.remove('flex');
    } else {
      this.progressThreshold.classList.remove('hidden');
      this.progressThreshold.classList.add('flex');
    }
  }

  calculateProgress(cartSubtotal, thresholdData) {
    if (thresholdData.length === 0) return 0;

    const minThreshold = thresholdData[0].threshold;
    const maxThreshold = thresholdData[thresholdData.length - 1].threshold;

    this.nextThreshold = null;
    let nextThresholdData;

    thresholdData.forEach((data) => {
      if (cartSubtotal < data.threshold && !nextThresholdData) {
        nextThresholdData = data;
      }
    });

    this.nextThreshold = nextThresholdData;

    if (cartSubtotal >= maxThreshold) {
      return 100;
    }

    if (thresholdData.length === 1) {
      return (cartSubtotal / maxThreshold) * 100;
    }

    if (cartSubtotal <= maxThreshold) {
      return (cartSubtotal / maxThreshold) * 100;
    }
    return (
      ((cartSubtotal - minThreshold) / (maxThreshold - minThreshold)) * 100
    );
  }

  calculateCartTotalWithGWP(cartData) {
    const parsedCartData = cartData ? JSON.parse(cartData.textContent) : null;

    if (!parsedCartData) return;
    const cartItems = parsedCartData.items;
    let cartTotal = 0;

    cartItems.forEach((item) => {
      if (item.properties?._gwp) return;
      cartTotal += item.final_line_price;
    });

    // eslint-disable-next-line consistent-return
    return cartTotal > 0 ? cartTotal / 100 : 0;
  }

  init() {
    this.setupIcons();
    this.renderProgress();
  }
}

if (!customElements.get('cart-incentives')) {
  customElements.define('cart-incentives', CartIncentives);
}
